import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { CustomFieldQuery } from '../api/useFetchCandidateProfiles';
import type { FormValue } from '../searchSchema';

export interface ExtendedCustomField {
  id: number;
  name: string;
  unitName: string | null;
  orderNumber: number;
  fieldType: string;
  custom_field_options?: Array<{ id: number; value: string }>;
}

interface UseCustomFieldSearchProps {
  onSubmit: (data: FormValue) => void;
  watch: () => FormValue;
  initialCustomFields?: CustomFieldQuery[];
  availableCustomFields?: ExtendedCustomField[];
}

// Range型の定義
interface RangeValue {
  min: string | number | null;
  max: string | number | null;
}

// 各フィールドの値を表す型
type FieldValue = string | number | number[] | RangeValue | null;

// CustomFieldのデータ構造
interface CustomFieldData {
  fieldId: number;
  fieldType: string;
  fieldName: string;
  value: FieldValue;
  options?: Array<{ id: number; value: string }>;
}

// 全体のstate
type CustomFieldsState = {
  [fieldId: number]: CustomFieldData;
};

// Simple debounce implementation
function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): T & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced as T & { cancel: () => void };
}

export default function useCustomFieldSearch({
  onSubmit,
  watch,
  initialCustomFields,
  availableCustomFields,
}: UseCustomFieldSearchProps) {
  // 単一のstateで全てを管理
  const [customFields, setCustomFields] = useState<CustomFieldsState>({});

  // 初回レンダーを追跡
  const isFirstRender = useRef(true);
  const lastRestoredFields = useRef<string>('');
  // 復元待ちのフィールド情報を保持
  const pendingRestore = useRef<{
    queries: CustomFieldQuery[];
    fields: ExtendedCustomField[];
  } | null>(null);

  // フィールドを追加
  const addCustomField = useCallback((field: ExtendedCustomField) => {
    setCustomFields((prev) => ({
      ...prev,
      [field.id]: {
        fieldId: field.id,
        fieldType: field.fieldType,
        fieldName: field.name,
        value: getInitialValue(field.fieldType),
        options: field.custom_field_options,
      },
    }));
  }, []);

  // 初期値を取得
  const getInitialValue = (fieldType: string): FieldValue => {
    switch (fieldType) {
      case 'date':
      case 'number':
        return { min: null, max: null };
      case 'short_text':
      case 'long_text':
        return '';
      case 'single_select':
        return null;
      case 'multiple_select':
        return [];
      default:
        return null;
    }
  };

  // 汎用的な値更新関数
  const updateFieldValue = useCallback((fieldId: number, value: FieldValue) => {
    setCustomFields((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        value,
      },
    }));
  }, []);

  // フィールドを削除
  const removeCustomField = useCallback((fieldId: number) => {
    setCustomFields((prev) => {
      const newState = { ...prev };
      delete newState[fieldId];
      return newState;
    });
  }, []);

  // 全てクリア
  const clearAllCustomFields = useCallback(() => {
    setCustomFields({});
  }, []);

  // CustomFieldQuery[]から状態を復元する関数
  const restoreCustomFields = useCallback(
    (customFieldQueries: CustomFieldQuery[], fields: ExtendedCustomField[]) => {
      if (!fields || fields.length === 0) {
        return;
      }

      // 既に復元済みかチェック
      const currentRestoredKey = JSON.stringify(
        customFieldQueries.map((cf) => ({ field_id: cf.field_id, content: cf.content }))
      );
      if (lastRestoredFields.current === currentRestoredKey) {
        return;
      }

      // 復元待ちの情報を保存
      pendingRestore.current = {
        queries: customFieldQueries,
        fields,
      };

      // まず全てのフィールドを追加
      customFieldQueries.forEach((query) => {
        const field = fields.find((f) => f.id === query.field_id);
        if (!field) return;

        // フィールドが既に追加されているかチェック（最新の状態を確認するため、コールバック形式で）
        setCustomFields((prev) => {
          if (prev[field.id]) {
            return prev;
          }
          return {
            ...prev,
            [field.id]: {
              fieldId: field.id,
              fieldType: field.fieldType,
              fieldName: field.name,
              value: getInitialValue(field.fieldType),
              options: field.custom_field_options,
            },
          };
        });
      });
    },
    []
  );

  // フィールドが追加されたら値を設定する
  useEffect(() => {
    if (!pendingRestore.current) return;

    const { queries, fields } = pendingRestore.current;

    // 必要なフィールドが全て追加されているかチェック
    const allFieldsAdded = queries.every((query) => {
      const field = fields.find((f) => f.id === query.field_id);
      return field && customFields[field.id];
    });

    if (allFieldsAdded) {
      // 全てのフィールドが追加されたら値を設定
      queries.forEach((query) => {
        const field = fields.find((f) => f.id === query.field_id);
        if (!field || !customFields[field.id]) return;

        let restoredValue: FieldValue;

        // contentの型に応じて値を復元
        if (
          typeof query.content === 'object' &&
          query.content !== null &&
          !Array.isArray(query.content)
        ) {
          // Range型（min/max）
          restoredValue = {
            min: query.content.min ?? null,
            max: query.content.max ?? null,
          };
        } else if (Array.isArray(query.content)) {
          // multiple_select
          restoredValue = query.content;
        } else if (typeof query.content === 'string' || typeof query.content === 'number') {
          // short_text, long_text, single_select, number
          restoredValue = query.content;
        } else {
          restoredValue = getInitialValue(field.fieldType);
        }

        // 値が既に設定されているかチェック（初期値でない場合）
        const currentValue = customFields[field.id].value;
        const isInitialValue =
          (field.fieldType === 'date' || field.fieldType === 'number') &&
          typeof currentValue === 'object' &&
          currentValue !== null &&
          'min' in currentValue &&
          'max' in currentValue &&
          currentValue.min === null &&
          currentValue.max === null;
        const isEmptyValue =
          currentValue === '' ||
          currentValue === null ||
          (Array.isArray(currentValue) && currentValue.length === 0);

        if (isInitialValue || isEmptyValue) {
          updateFieldValue(field.id, restoredValue);
        }
      });

      // 復元完了
      lastRestoredFields.current = JSON.stringify(
        queries.map((cf) => ({ field_id: cf.field_id, content: cf.content }))
      );
      pendingRestore.current = null;
    }
  }, [customFields, updateFieldValue]);

  // API送信用のデータに変換（純粋関数）
  const collectCustomFieldValues = useCallback((): CustomFieldQuery[] => {
    return Object.values(customFields)
      .filter((field) => hasValue(field))
      .map((field) => ({
        field_id: field.fieldId,
        content: formatValueForAPI(field),
      }));
  }, [customFields]);

  // 値が存在するかチェック（純粋関数）
  const hasValue = (field: CustomFieldData): boolean => {
    const { value, fieldType } = field;

    switch (fieldType) {
      case 'short_text':
      case 'long_text':
        return typeof value === 'string' && value.trim() !== '';
      case 'single_select':
        return value !== null && value !== undefined;
      case 'multiple_select':
        return Array.isArray(value) && value.length > 0;
      case 'date':
      case 'number':
        if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
          return value.min !== null || value.max !== null;
        }
        return false;
      default:
        return false;
    }
  };

  // API用に値をフォーマット（純粋関数）
  const formatValueForAPI = (
    field: CustomFieldData
  ): string | number | number[] | { min?: string | number; max?: string | number } => {
    const { value, fieldType } = field;

    if (fieldType === 'date' || fieldType === 'number') {
      if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
        const result: { min?: string | number; max?: string | number } = {};
        if (value.min !== null) result.min = value.min;
        if (value.max !== null) result.max = value.max;
        return result;
      }
    }

    // Handle null and other non-conforming types
    if (value === null) {
      return '';
    }
    if (typeof value === 'string' || typeof value === 'number' || Array.isArray(value)) {
      return value;
    }

    return '';
  };

  // debounced検索実行
  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        const formData = watch();
        const customFieldsData = collectCustomFieldValues();
        const extendedData = {
          ...formData,
          customFields: customFieldsData,
        };
        onSubmit(extendedData);
      }, 300),
    [watch, onSubmit, collectCustomFieldValues]
  );

  // 初期値から復元
  useEffect(() => {
    if (
      initialCustomFields &&
      initialCustomFields.length > 0 &&
      availableCustomFields &&
      availableCustomFields.length > 0
    ) {
      // 既に復元済みかチェック（同じ内容なら再復元しない）
      const currentRestoredKey = JSON.stringify(
        initialCustomFields.map((cf) => ({ field_id: cf.field_id, content: cf.content }))
      );

      if (lastRestoredFields.current !== currentRestoredKey) {
        restoreCustomFields(initialCustomFields, availableCustomFields);
        // 復元後に検索を実行
        setTimeout(() => {
          debouncedSearch();
        }, 100);
      }
    }
  }, [initialCustomFields, availableCustomFields, restoreCustomFields, debouncedSearch]);

  // customFieldsが変更されたら検索を実行
  useEffect(() => {
    // 初回レンダー時は実行しない
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // CustomFieldが1つ以上ある場合のみ実行
    if (
      Object.keys(customFields).length > 0 ||
      // または、以前CustomFieldがあったが全て削除された場合
      (Object.keys(customFields).length === 0 && !isFirstRender.current)
    ) {
      // 復元直後の検索は既に実行されているので、少し遅延させて重複を防ぐ
      const timeoutId = setTimeout(() => {
        debouncedSearch();
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [customFields, debouncedSearch]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return {
    customFields,
    addCustomField,
    updateFieldValue,
    removeCustomField,
    clearAllCustomFields,
    restoreCustomFields,
    hasCustomFieldValues: useCallback(() => {
      return Object.values(customFields).some((field) => hasValue(field));
    }, [customFields]),
    collectCustomFieldValues,
    // 便利なヘルパー関数
    getFieldValue: useCallback((fieldId: number) => customFields[fieldId]?.value, [customFields]),
    getSelectedFields: useCallback(() => Object.values(customFields), [customFields]),
  };
}
